import React, { FC } from 'react';

import composeFullAddress from 'utils/composeFullAddress';
import parsePlacesResult, { unitToKeep } from 'utils/parsePlacesResult';

import {
  Flex,
  Modal,
  Stack,
  Text,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Switch,
  Radio,
  RadioGroup,
} from '@chakra-ui/react';

import { Form, Formik } from 'formik';

import { UrlInput } from '../UrlInput';
import { UrlSwitch } from '../UrlSwitch';
import { PackageSelect } from '../PackageSelect';

import FormikImageInput from '../FormikImageInput';
import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import FormikMultiSelect from 'components/primitives/FormikMultiSelect';
import PlacesAutocomplete from 'components/primitives/PlacesAutocomplete';

import getSortedData from 'utils/getSortedData';
import { days, pickupFrequency } from 'constants/weekdays';

import { selectors } from 'store/slices/washhub';

import { getInitialValues, validationSchema } from './SiteModal.utils';
import { SiteModalViewProps } from './SiteModal.props';
import { SiteTypeEnum } from 'types/site';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const TYPES = Object.values(SiteTypeEnum);

const SiteModalView: FC<SiteModalViewProps> = (props) => {
  const {
    isOpen,
    onClose,
    onSubmit,
    siteId,
    site,
    isSiteEdit,
    isLoading,
    washHubId,
  } = props;

  const washHub = selectors.useSelectedWashHub();

  const modalTitle = isSiteEdit
    ? `Update site - ${site?._id}`
    : `Create new site`;

  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent p="20px" borderRadius="15px">
        <ModalCloseButton _focus={{ outline: 'none' }} top="5" />
        <ModalHeader textAlign="center" fontWeight="600" color="#262626">
          {modalTitle}
        </ModalHeader>
        <ModalBody>
          <Flex direction="column">
            <Formik
              enableReinitialize
              onSubmit={onSubmit}
              initialValues={getInitialValues(site)}
              validationSchema={validationSchema}
              validate={(values) => {
                let errors: any = {};
                if (isSiteEdit) {
                  if (
                    !values.portalName &&
                    !!getInitialValues(site)?.portalName
                  ) {
                    errors.portalName = 'Portal name cannot be empty';
                  }

                  return errors;
                }
              }}>
              {({ values, isValid, errors, setFieldValue, initialValues }) => (
                <Form>
                  <Flex gap="15px">
                    <FormikInput
                      name="name"
                      label="Site Name (*)"
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                    />
                    <FormikImageInput
                      name="logo"
                      label="Logo"
                      siteId={siteId}
                      aspectRatio={1 / 1}
                      containerProps={{ flex: 1 }}
                      isDisabled={!Boolean(values?.portal)}
                    />
                    <FormikImageInput
                      name="banner"
                      label="Banner"
                      siteId={siteId}
                      aspectRatio={5.81 / 1}
                      containerProps={{ flex: 1 }}
                      isDisabled={!Boolean(values?.portal)}
                    />
                  </Flex>
                  <Flex mt={2} gap="15px">
                    <FormikSelectInput
                      placeholder="Select"
                      label="Site Type"
                      containerProps={{ flex: 1 }}
                      name="siteType"
                      labelProps={labelProps}>
                      {getSortedData(TYPES)?.map((s) => (
                        <option key={s} value={s}>
                          {s || ''}
                        </option>
                      ))}
                    </FormikSelectInput>
                  </Flex>
                  <Flex mt={2} gap="15px">
                    <FormikSelectInput
                      containerProps={{ flex: 1 }}
                      placeholder="Select"
                      label="Delivery Frequency (*)"
                      name="deliveryFrequency">
                      {getSortedData(pickupFrequency).map((w) => (
                        <option key={w} value={w}>
                          {w || ''}
                        </option>
                      ))}
                    </FormikSelectInput>
                    {['Weekly', 'Bi-Weekly', 'Monthly', 'Per Event'].includes(
                      values?.deliveryFrequency,
                    ) && (
                      <>
                        <FormikMultiSelect
                          options={days}
                          containerProps={{ flex: 1 }}
                          placeholder="Select"
                          label="Delivery Day Of The Week"
                          name="deliveryDayOfTheWeek"
                        />
                      </>
                    )}

                    <PlacesAutocomplete
                      name="address"
                      labelText="Address (*)"
                      labelProps={{ ...labelProps, fontWeight: 'bold' }}
                      defaultInputValue={composeFullAddress(site?.address)}
                      onSelect={(result) => {
                        const parsed = parsePlacesResult(result);

                        // addressLine1 is the street; addressLine2 the unit
                        // Google gave, else the one typed, never a copied
                        // address.
                        setFieldValue('address', {
                          ...parsed,
                          zip: parsed.zip || '00000',
                          addressLine2: unitToKeep(
                            parsed.addressLine2,
                            values?.address?.addressLine2,
                          ),
                        });
                      }}
                    />
                  </Flex>
                  <Flex mt={2} gap="15px">
                    <FormikInput
                      type="number"
                      name="capacity"
                      label="Capacity"
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                    />
                    <FormikInput
                      name="washHub"
                      label="Wash Hub"
                      labelProps={labelProps}
                      isReadOnly
                      value={washHub?.name || ''}
                      containerProps={{ flex: 1 }}
                    />
                    <FormikInput
                      name="websiteUrl"
                      label="Website Show List"
                      placeholder="your URL here"
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                    />
                    <FormikInput
                      name="qrLandingUrl"
                      label="QR Landing Page URL"
                      type="text"
                      placeholder="https://..."
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                    />
                  </Flex>
                  <FormikInput
                    name="deliveryInstructions"
                    label="Delivery instructions"
                    labelProps={labelProps}
                    containerProps={{ flex: 1, mt: 5 }}
                  />
                  <FormikInput
                    name="pickupInstructions"
                    label="Pickup instructions"
                    labelProps={labelProps}
                    containerProps={{ flex: 1, mt: 5 }}
                  />
                  <Flex direction="column">
                    <Text {...labelProps} mt="20px" fontWeight="bold">
                      Invoice Status
                    </Text>
                    <Flex mt="10px" align="flex-end" gap="20px">
                      <Text mb="2px">Invoice by Delivery</Text>
                      <RadioGroup
                        colorScheme="primary"
                        value={
                          typeof values?.invoice === 'boolean'
                            ? values?.invoice
                              ? '1'
                              : '0'
                            : undefined
                        }
                        onChange={(e) => {
                          setFieldValue('invoice', e === '1' ? true : false);
                        }}>
                        <Radio value="1">Yes</Radio>
                        <Radio value="0" ml="10px">
                          No
                        </Radio>
                      </RadioGroup>
                    </Flex>
                    {errors?.invoice && (
                      <Text fontSize="sm" color="red.500">
                        {errors?.invoice}
                      </Text>
                    )}
                  </Flex>
                  <Flex maxW="385px" w="100%">
                    <PackageSelect
                      values={values?.packages}
                      washHubId={washHubId}
                    />
                  </Flex>

                  <Flex direction="column">
                    <Text {...labelProps} mt="20px" fontWeight="bold">
                      Status
                    </Text>
                    <Flex mt="10px" align="center" gap="20px">
                      <Text>Inactive</Text>
                      <Switch
                        isChecked={values?.isSuspended}
                        onChange={(e) => {
                          setFieldValue('isSuspended', e.target.checked);
                        }}
                        colorScheme="primary"
                      />
                    </Flex>
                  </Flex>

                  <Stack>
                    <UrlSwitch
                      name="portal"
                      onChange={(e) => {
                        if (e.target.checked === false) {
                          if (
                            !!initialValues.portalName &&
                            !values.portalName
                          ) {
                            setFieldValue(
                              'portalName',
                              initialValues.portalName,
                            );
                          }
                        }
                      }}
                    />
                    <UrlInput name="portalName" />
                  </Stack>
                  <Flex my={12} flexDirection="row" justifyContent="center">
                    <Button
                      variant="outline"
                      marginRight="10px"
                      w="134px"
                      onClick={onClose}
                      isDisabled={isLoading || values._isUploading}>
                      Cancel
                    </Button>
                    <Button
                      isDisabled={!isValid}
                      w="134px"
                      type="submit"
                      isLoading={isLoading || values._isUploading}>
                      {!isSiteEdit ? `Create Site` : `Update Site`}
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SiteModalView;
